// The column-visibility model shared by `Table` (which renders the columns) and `ColumnsButton`
// (which lets the user pick them). Both read and write the SAME per-user map — the consuming app
// keeps it under `visibilityMap[visibilityKey]` and persists it whole (see each app's
// `context/useVisibility`), so the two components must agree on its shape down to the defaults.
//
// Stored shape, one entry per column key:
//
//     { [key]: { position: <number>, visibility: <boolean> } }
//
// `position` is the left-to-right order that dragging a header writes; `visibility` is the
// checkbox. Two rules make a stored map survive later code changes:
//
//   - A key MISSING from the map counts as VISIBLE at its declared position. That is what lets a
//     column added in a later release show up for users who already have a saved layout, instead
//     of staying silently hidden for exactly the people who use the feature.
//   - Entries for keys that are not in the current `columns` are carried through untouched. A page
//     may build its column list conditionally (a privilege, a tab), and a user's choice for a
//     column that happens to be absent right now must not be erased by an unrelated toggle.
//
// Never rename a column `key`: to a stored map a rename reads as "a new column", so every user's
// choice for the old one is lost. Change the `header` instead.

/**
 * What to call a column in a header cell or a checkbox row. A column with no `header` falls back
 * to its capitalised key, which is how the table has always labelled those.
 */
export function columnLabel(col) {
  if (!col) return "";
  if (col.header) return col.header;
  const key = col.key || "";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** Is this column visible? Anything but an explicit `false` is. */
export function isColumnVisible(state, key) {
  return state?.[key]?.visibility !== false;
}

/**
 * Every current column's state, stored values first and declaration order as the fallback.
 * Entries for columns that aren't currently rendered are preserved as-is.
 */
export function resolveColumnState(columns, stored) {
  const state = { ...(stored || {}) };
  (columns || []).forEach((col, index) => {
    const saved = stored?.[col.key];
    state[col.key] = {
      position: typeof saved?.position === "number" ? saved.position : index,
      visibility: saved?.visibility !== false,
    };
  });
  return state;
}

/**
 * The columns in display order. Declaration order breaks ties, so a column whose position was
 * never stored (a new one) lands where the code puts it rather than in an arbitrary spot.
 */
export function orderColumns(columns, state) {
  return (columns || [])
    .map((col, index) => ({
      col,
      index,
      position:
        typeof state?.[col.key]?.position === "number"
          ? state[col.key].position
          : index,
    }))
    .sort((a, b) => a.position - b.position || a.index - b.index)
    .map((entry) => entry.col);
}

/** The columns to actually render, in display order. */
export function visibleColumns(columns, state) {
  return orderColumns(columns, state).filter((col) =>
    isColumnVisible(state, col.key),
  );
}

/** How many of the current columns are hidden — the count on the "Columns" button. */
export function hiddenColumnCount(columns, state) {
  return (columns || []).filter((col) => !isColumnVisible(state, col.key))
    .length;
}

/**
 * Flip one column. Returns the next state, or `null` when the toggle was refused because it would
 * have left the table with no columns at all.
 */
export function toggleColumn(columns, state, key) {
  const resolved = resolveColumnState(columns, state);
  const hiding = isColumnVisible(resolved, key);
  if (hiding) {
    const visible = (columns || []).filter((col) =>
      isColumnVisible(resolved, col.key),
    );
    if (visible.length <= 1) return null;
  }
  return {
    ...resolved,
    [key]: { ...resolved[key], visibility: !hiding },
  };
}

/** Show every current column again, keeping the order the user arranged. */
export function showAllColumns(columns, state) {
  const resolved = resolveColumnState(columns, state);
  const next = { ...resolved };
  (columns || []).forEach((col) => {
    next[col.key] = { ...resolved[col.key], visibility: true };
  });
  return next;
}

/**
 * Move `movedKey` to where `targetKey` currently sits.
 *
 * Positions belong to EVERY column, but a drag only ever names two visible ones — so the move is
 * translated into the full column order and all of it is renumbered. Renumbering just the visible
 * columns would leave hidden ones on their old positions, colliding with the fresh ones and
 * scrambling the order the moment anything is unhidden.
 */
export function moveColumn(columns, state, movedKey, targetKey) {
  const resolved = resolveColumnState(columns, state);
  const keys = orderColumns(columns, resolved).map((col) => col.key);
  const from = keys.indexOf(movedKey);
  const to = keys.indexOf(targetKey);
  if (from === -1 || to === -1 || from === to) return null;

  keys.splice(from, 1);
  keys.splice(to, 0, movedKey);

  const next = { ...resolved };
  keys.forEach((key, index) => {
    next[key] = { visibility: isColumnVisible(resolved, key), position: index };
  });
  return next;
}
